import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import MainLayout from "../components/common/MainLayout";
import LoginPage from "../pages/login";
import HomePage from "../pages/home";
import NotFound from "../pages/notFound";
import useUserRoles from "../hooks/useUserRoles";
import usePermissions from "../hooks/usePermissions";
import ProfilePage from "../pages/profile";
import AgencyUserRoles from "../components/AgencyUserRoles";
import AdRequestSection from "../components/AdRequest";
import AdRequestDetailPage from "../components/AdRequest/AdRequestDetailPage";
import AdDeliverSection from "../components/AdDeliver";
import AdModerationSection from "../components/AdModeration";
import AdLaunchSection from "../components/AdLaunch";
import AdRateCardSection from "../components/AdRateCard";
import CategoryTrackMappingSection from "../components/CategoryTrackMapping";
import AdReccoSection from "../components/AdRecco";
import AdsSection from "../components/Ads";
import AdSprintsSection from "../components/AdSprints";
import AdIntelligencePage from "../pages/AdIntelligence";
import useZustandStore from "../zustandStore/useZustandStore";
import PendingApproval from "../pages/PendingApproval";
import { getFirstAllowedDashboardPath } from "../utils/constants/permissions";

const AllRoutes = () => {
  const currentUser = useZustandStore((val) => val?.currentUser);
  const { isAdmin, isExternal, isPending } = useUserRoles();
  const { hasRouteAccess } = usePermissions();
  const isLoggedIn = !!currentUser?.roles;


  return (
    <MainLayout>
      <Routes>
        <Route
          path="/"
          element={
            isAdmin || isExternal ? (
              <Navigate to="/dashboard" />
            ) : isPending ? (
              <Navigate to="/pending-approval" />
            ) : isLoggedIn ? (
              // Fallback for logged in but unauthorized/unknown status users -> send to pending/error page
              // to break the / -> /login -> /dashboard -> / loop
              <Navigate to="/pending-approval" />
            ) : (
              // Public landing page for unauthenticated users so the homepage
              // is not behind login (required for Google OAuth verification)
              <HomePage />
            )
          }
        />

        {/* login route */}
        <Route
          path="/login"
          element={
            isLoggedIn ? (
              isPending ? (
                <Navigate to="/pending-approval" />
              ) : isAdmin || isExternal ? (
                <Navigate to="/dashboard" />
              ) : (
                // Fallback for logged in but unauthorized users
                <Navigate to="/pending-approval" />
              )
            ) : (
              <LoginPage />
            )
          }
        />

        {/* Pending Approval Route */}
        <Route
          path="/pending-approval"
          element={
            isLoggedIn && !isAdmin && !isExternal ? (
              <PendingApproval />
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        <Route
          path="/dashboard"
          element={
            isAdmin || isExternal ? (
              <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ads"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ads") ? (
                <AdsSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/agency_sprints"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/agency_sprints") ? (
                <AdSprintsSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ad-upload"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-upload") ? (
                <AdDeliverSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ad-request"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-request") ? (
                <AdRequestSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/dashboard/ad-request/assigned"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-request/assigned") ? (
                <AdRequestSection view="assigned" />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />
        {/* Declared after /assigned, though order does not decide it: React
            Router 6 ranks a static segment above a dynamic one, so /assigned
            never falls through to :id.

            Guarded on the PARENT path on purpose — getRequiredPermissionsForRoute
            is an exact dictionary lookup, so "/dashboard/ad-request/24" would
            match no key, return [], and let anyone in. The detail page needs
            exactly what the list needs. */}
        <Route
          path="/dashboard/ad-request/:id"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-request") ? (
                <AdRequestDetailPage />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ad-moderation"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-moderation") ? (
                <AdModerationSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ad-launch"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-launch") ? (
                <AdLaunchSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/agency-user-roles"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/agency-user-roles") ? (
                <AgencyUserRoles />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ad-rate-card"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-rate-card") ? (
                <AdRateCardSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/category-track-mapping"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/category-track-mapping") ? (
                <CategoryTrackMappingSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ad-recco"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-recco") ? (
                <AdReccoSection />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        <Route
          path="/dashboard/ad-intelligence"
          element={
            isAdmin || isExternal ? (
              hasRouteAccess("/dashboard/ad-intelligence") ? (
                <AdIntelligencePage />
              ) : (
                <Navigate to={getFirstAllowedDashboardPath(hasRouteAccess)} replace />
              )
            ) : (
              <Navigate to="/" />
            )
          }
        />

        {/* Profile route */}
        <Route
          path="/profile"
          element={isLoggedIn ? <ProfilePage /> : <Navigate to="/login" />}
        />

        {/* Any random route will be Redirected to /404 */}
        <Route path="*" element={<Navigate to="/404" />} />

        {/* 404 route made just to hide side nav and navbar  */}
        <Route path="/404" element={<NotFound />} />
      </Routes>
    </MainLayout>
  );
};

export default AllRoutes;
